import { Component, Input, Output, EventEmitter, signal, computed, OnInit, OnChanges, SimpleChanges, inject, ViewChild, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TegelModule } from '@scania/tegel-angular-17';
import { AdminApiService, Section, Desk, SectionConfig, CommunicationRequest } from '../../../core/services/repository/flexseat/admin-api.service';
import { SweetAlertOptions } from 'sweetalert2';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-building-admin',
  standalone: true,
  imports: [CommonModule, TegelModule, FormsModule],
  templateUrl: './building-admin.component.html',
  styleUrls: ['./building-admin.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BuildingAdminComponent implements OnInit, OnChanges {
  @Input() buildingId = '';
  @ViewChild("adminModalMesaBloqueio", { static: true })
  adminModalMesaBloqueio!: HTMLTdsModalElement;
  @Output() hoverDesk = new EventEmitter<string | null>();
  @Output() leaveDesk = new EventEmitter<void>();
  @Output() selectDesk = new EventEmitter<string | null>();
  @Output() confirmSelection = new EventEmitter<number[]>();

  public visible = signal(true);

  public selectedSection?: Section;
  public selectedDesks = new Map<number, Desk>();

  public departmentInput: string = '';
  public exceptionsInput: string = '';
  public statement: string = '';
  public statementStart: string = '';
  public statementEnd: string = '';

  public selectedAreas: {value: string; label: string}[] = [];
  public selectedExceptions: {value: string; label: string}[] = [];

  public searchTerm = signal('');
  public showLockedOnly = signal(false);

  public isSaveAllowedAreasEnabled = signal(false);
  public isSaveCommunicationEnabled = signal(false);

  public showBlockModal = signal(false);
  public blockModalMesasInput: string = '';
  public blockModalToggle: boolean = false;
  public blockModalDataInicio: string = '';
  public blockModalDataFim: string = '';
  public blockModalIndeterminado: boolean = false;
  public blockModalObservacoes: string = '';
  public blockModalDiasWeek: { dia: string; label: string; checked: boolean }[] = [
    { dia: 'segunda', label: 'Segunda', checked: false },
    { dia: 'terça', label: 'Terça', checked: false },
    { dia: 'quarta', label: 'Quarta', checked: false },
    { dia: 'quinta', label: 'Quinta', checked: false },
    { dia: 'sexta', label: 'Sexta', checked: false },
    { dia: 'sábado', label: 'Sábado', checked: false },
    { dia: 'domingo', label: 'Domingo', checked: false }
  ];

  public filteredDesks = computed(() => {
    const term = String(this.searchTerm()).trim().toLowerCase();
    let list = this.apiDesks();

    if (term) {
      list = list.filter(d => (d.name || d.table_id + '').toLowerCase().includes(term));
    }
    if (this.showLockedOnly()) {
      list = list.filter(d => !!d.id);
    }
    return list;
  });

  private readonly adminApi = inject(AdminApiService);

  private sections: Section[] = [];
  private apiDesks = signal<Desk[]>([]);

  private originalSelectedAreas: string[] = [];
  private originalSelectedExceptions: string[] = [];
  private originalBlocked: boolean = false;
  private originalCheckin: boolean = false;
  private originalStatement: string = '';
  private originalStatementStart: string = '';
  private originalStatementEnd: string = '';

  // Calcula a data mínima para o início do bloqueio (15 dias a partir de hoje)
  get minDataInicio(): string {
    const date = new Date();
    date.setDate(date.getDate() + 15);
    return this.dateToInputFormat(date);
  }

  // Calcula a data mínima para o fim do bloqueio (deve ser após a data de início)
  get minDataFim(): string {
    if (!this.blockModalDataInicio) {
      return '';
    }
    const date = new Date(this.blockModalDataInicio);
    date.setDate(date.getDate() + 1); // Próximo dia após a data de início
    return this.dateToInputFormat(date);
  }

  get selectedDesksArray(): Desk[] {
    return Array.from(this.selectedDesks.values());
  }

  constructor() {}

  ngOnInit(): void {
    this.adminApi.getSections().subscribe({
      next: (s) => {
        this.sections = s;
        this.updateSelectedSection();
      },
      error: () => {
        this.sections = [];
        this.updateSelectedSection();
      }
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['buildingId']) {
      this.updateSelectedSection();
    }
  }

  public async saveSectionConfig(): Promise<void> {
    if (!this.selectedSection) return;

    const saveSectionOptions: SweetAlertOptions = {
      title: 'Confirmar alterações',
      text: 'Deseja realmente salvar as alterações de configuração da seção?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não'
    };

    const result = await Swal.fire(saveSectionOptions);
    
    if (!result.isConfirmed) return;

    const dept = this.selectedAreas.map(function(area) { return area.value; });
    const exc = this.selectedExceptions.map(function(exception) { return exception.value; });
    const config: SectionConfig = {
      id: this.selectedSection.id,
      blocked: !!this.selectedSection.blocked,
      department: dept,
      exceptions: exc,
      checkin: !!this.selectedSection.checkin
    };

    this.adminApi.putSectionConfig(config).subscribe({
      next: () => {
        this.selectedSection = {
          ...this.selectedSection!,
          department: dept,
          exceptions: exc,
          blocked: config.blocked,
          checkin: config.checkin,
        };
        this.resetOriginalValues();
        Swal.fire({ icon: 'success', title: 'Alterações salvas com sucesso!' });
      },
      error: () => {
        Swal.fire({ icon: 'error', title: 'Erro ao salvar', text: 'Tente novamente.' });
      }
    });
  }

  public async saveCommunication(): Promise<void> {
    if (!this.selectedSection) return;

    const saveCommunicationOptions: SweetAlertOptions = {
      title: 'Confirmar comunicado',
      text: 'Deseja realmente salvar o comunicado e as datas?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não'
    };

    const result = await Swal.fire(saveCommunicationOptions);

    if (!result.isConfirmed) return;

    const comunicadoConfig: CommunicationRequest = {
      id: this.selectedSection.id,
      statement: this.statement,
      statement_start: this.dateInputToIso(this.statementStart) || '',
      statement_end: this.dateInputToIso(this.statementEnd) || ''
    };

    this.adminApi.putCommunication(comunicadoConfig).subscribe({
      next: () => {
        this.selectedSection = {
          ...this.selectedSection!,
          statement: this.statement,
          statement_start: comunicadoConfig.statement_start,
          statement_end: comunicadoConfig.statement_end
        };
        this.resetOriginalValues();
        Swal.fire({ icon: 'success', title: 'Comunicado salvo com sucesso!' });
      },
      error: () => {
        Swal.fire({ icon: 'error', title: 'Erro ao salvar comunicado', text: 'Tente novamente.' });
      }
    });
  }

  public async removeCommunication(): Promise<void> {
    if (!this.selectedSection) return;

    const removeCommunicationOptions: SweetAlertOptions = {
      title: 'Remover comunicado',
      text: 'Deseja remover o comunicado e limpar as datas desta seção?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar'
    };

    const result = await Swal.fire(removeCommunicationOptions);

    if (!result.isConfirmed) return;

    this.statement = '';
    this.statementStart = '';
    this.statementEnd = '';

    const comunicadoConfig: CommunicationRequest = {
      id: this.selectedSection.id,
      statement: null,
      statement_start: null,
      statement_end: null
    };

    this.adminApi.putCommunication(comunicadoConfig).subscribe({
      next: () => {
        this.selectedSection = {
          ...this.selectedSection!,
          statement: null as any,
          statement_start: null as any,
          statement_end: null as any
        };
        this.resetOriginalValues();
        Swal.fire({ icon: 'success', title: 'Comunicado removido com sucesso!' });
      },
      error: () => {
        Swal.fire({ icon: 'error', title: 'Erro ao remover comunicado', text: 'Tente novamente.' });
      }
    });
  }

  public toggleSelect(seat: Desk, event?: Event) {
    const id = seat.table_id;

    if (id == null) {
      return;
    }

    if (this.selectedDesks.has(id)) {
      this.selectedDesks.delete(id);
    } else {
      this.selectedDesks.set(id, seat);
    }
  }

  public toggleSelectFromCheckbox(seat: Desk, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.toggleSelect(seat, event);
  }

  public confirmSelected(): void {
    const selectedDesks = this.selectedDesksArray;

    if (selectedDesks.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Selecione pelo menos uma mesa' });
      return;
    }

    this.blockModalMesasInput = selectedDesks.map(s => s.name || s.table_id).join(', ');

    selectedDesks.forEach(d => {
      if (d.username == null) (d as any).username = '';
    });

    this.blockModalToggle = true;
    this.blockModalDataInicio = '';
    this.blockModalDataFim = '';
    this.blockModalIndeterminado = false;
    this.blockModalObservacoes = '';
    this.blockModalDiasWeek.forEach(d => d.checked = false);

    const mesaComBloqueio = selectedDesks.find(d => d.id);
    if (mesaComBloqueio) {
      this.blockModalToggle = mesaComBloqueio.active !== null ? mesaComBloqueio.active : true;
      this.blockModalDataInicio = mesaComBloqueio.start_date ? this.isoToDateInput(mesaComBloqueio.start_date) : '';
      this.blockModalDataFim = mesaComBloqueio.end_date ? this.isoToDateInput(mesaComBloqueio.end_date) : '';
      this.blockModalIndeterminado = !mesaComBloqueio.start_date && !mesaComBloqueio.end_date;
      this.blockModalObservacoes = mesaComBloqueio.observation || '';

      const diasSelecionados = mesaComBloqueio.weekday || [];
      this.blockModalDiasWeek.forEach(dia => {
        const diasMap: { [key: string]: number } = {
          'domingo': 0,
          'segunda': 1,
          'terça': 2,
          'quarta': 3,
          'quinta': 4,
          'sexta': 5,
          'sábado': 6
        };
        dia.checked = diasSelecionados.includes(diasMap[dia.dia]);
      });
    }

    this.adminModalMesaBloqueio.showModal();
  }

  public confirmBlockDesk(): void {
    const selectedDesks = this.selectedDesksArray;

    const allUsersValid = selectedDesks.every(d => d.username && d.username.trim().length > 0);
    if (!allUsersValid) {
      Swal.fire({ icon: 'warning', title: 'Atenção', text: 'O campo de usuário não deve ficar vazio' });
      return;
    }

    const diasMap: { [key: string]: number } = {
      'domingo': 0,
      'segunda': 1,
      'terça': 2,
      'quarta': 3,
      'quinta': 4,
      'sexta': 5,
      'sábado': 6,

    };

    const diasSelecionadosNumeros = this.blockModalIndeterminado
      ? []
      : this.blockModalDiasWeek
          .filter(d => d.checked)
          .map(d => diasMap[d.dia]);

    selectedDesks.forEach(desk => {
      (desk as any).active = this.blockModalToggle;
      (desk as any).start_date = this.blockModalIndeterminado ? null : this.blockModalDataInicio;
      (desk as any).end_date = this.blockModalIndeterminado ? null : this.blockModalDataFim;
      (desk as any).weekday = diasSelecionadosNumeros;
      (desk as any).observation = this.blockModalObservacoes;
      if (desk.id === undefined) {
        (desk as any).id = null;
      }
    });

    this.adminApi.putBlockDesks(selectedDesks).subscribe({
      next: () => {
        Swal.fire({ icon: 'success', title: 'Bloqueios salvos com sucesso!' });
        if (this.blockModalIndeterminado) {
          this.blockModalDiasWeek.forEach(d => d.checked = false);
        }
        this.showBlockModal.set(false);
        this.selectedDesks.clear();
        this.fetchDesksForSelectedSection();
      },
      error: () => {
        Swal.fire({ icon: 'error', title: 'Erro ao salvar bloqueios', text: 'Tente novamente.' });
      }
    });
  }

  public isConfirmBlockEnabled(): boolean {
    const selectedDesks = this.selectedDesksArray;
    if (selectedDesks.length === 0) return false;

    const allUsersValid = selectedDesks.every(d => d.username && d.username.trim().length > 0);
    if (!allUsersValid) return false;

    if (this.blockModalIndeterminado) {
      return true;
    }

    const hasStart = !!this.blockModalDataInicio && this.blockModalDataInicio.trim().length > 0;
    const hasEnd = !!this.blockModalDataFim && this.blockModalDataFim.trim().length > 0;
    if (!hasStart || !hasEnd) return false;

    const hasDaySelected = this.blockModalDiasWeek.some(d => d.checked);
    return hasDaySelected;
  }

  public hasBlockedSelection(): boolean {
    return this.selectedDesksArray.some(d => d.id != null);
  }

  public removeBlockDesk(): void {
    const selectedDesks = this.selectedDesksArray;
    if (selectedDesks.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Selecione pelo menos uma mesa' });
      return;
    }

    const mesasComBloqueio = selectedDesks.filter(d => d.id != null);
    const mesasTexto = mesasComBloqueio.map(d => d.name || d.table_id).join(', ');
    Swal.fire({
      icon: 'warning',
      title: 'Confirmar remoção',
      text: `Tem certeza que quer remover o bloqueio das mesas ${mesasTexto}?`,
      showCancelButton: true,
      confirmButtonText: 'Remover Bloqueio',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (!result.isConfirmed) return;

      const ids = mesasComBloqueio
        .map(d => d.id)
        .filter((id): id is number => id != null);
      if (ids.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Nenhuma mesa possui bloqueio para remover' });
        return;
      }
      this.adminApi.deleteDeskBlocks(ids).subscribe({
        next: () => {
          Swal.fire({ icon: 'success', title: 'Bloqueios removidos com sucesso!' });
          this.showBlockModal.set(false);
          this.adminModalMesaBloqueio.closeModal();
          this.selectedDesks.clear();
          this.fetchDesksForSelectedSection();
        },
        error: () => {
          Swal.fire({ icon: 'error', title: 'Erro ao remover bloqueios', text: 'Tente novamente.' });
        }
      });
    });
  }

  public checkForChanges(): void {
    const currentAreas = this.selectedAreas
      .map(function(area) {
        return area.value;
      })
      .join(',');
  
    const originalAreas = this.originalSelectedAreas.join(',');
  
    const currentExceptions = this.selectedExceptions
      .map(function(exception) {
        return exception.value;
      })
      .join(',');
  
    const originalExceptions = this.originalSelectedExceptions.join(',');
  
    const hasChanges =
      currentAreas !== originalAreas ||
      currentExceptions !== originalExceptions ||
      (!!this.selectedSection?.blocked) !== this.originalBlocked ||
      (!!this.selectedSection?.checkin) !== this.originalCheckin;
  
    this.isSaveAllowedAreasEnabled.set(hasChanges);
  }

  public checkCommunicationChanges(): void {
    const hasChanges =
      this.statement !== this.originalStatement ||
      this.statementStart !== this.originalStatementStart ||
      this.statementEnd !== this.originalStatementEnd;

    const isValid =
      this.statement.trim().length > 0 &&
      this.statementStart.trim().length > 0 &&
      this.statementEnd.trim().length > 0;

    this.isSaveCommunicationEnabled.set(hasChanges && isValid);
  }

  public onDepartmentInput(event: Event): void {
    const input = (event.target as HTMLInputElement).value.toUpperCase();
    this.departmentInput = input;
  }
  
  public onDepartmentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }
  
    event.preventDefault();
  
    this.addArea();
  }

  public removeArea(value: string): void {
    this.selectedAreas = this.selectedAreas.filter(function(area) {
      return area.value !== value;
    });

    this.checkForChanges();
  }

  public onExceptionInput(event: Event): void {
    const input = (event.target as HTMLInputElement).value.toUpperCase();
    this.exceptionsInput = input;
  }
  
  public onExceptionKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }
  
    event.preventDefault();
  
    this.addException();
  }

  public removeException(value: string): void {
    this.selectedExceptions = this.selectedExceptions.filter(function(exception) {
      return exception.value !== value;
    });

    this.checkForChanges();
  }

  private close() {
    this.visible.set(false);
  }

  private toggleLock(seat: { id: string; locked: boolean }) {
    seat.locked = !seat.locked;
  }

  private toggleLockedOnly() {
    this.showLockedOnly.update(v => !v);
  }

  private isPrivateAreaValid(): boolean {
    if (this.selectedSection?.blocked) {
      return this.departmentInput.trim().length > 0;
    }
    return true;
  }

  // Converte uma Date para o formato YYYY-MM-DD
  private dateToInputFormat(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private parseBuildingId(id: string): { building?: number; region?: string } {
    if (!id) return {};
    const parts = id.split('-');
    const buildingPart = parts[0] || '';
    const regionPart = parts[1] || '';
    let buildingNum: number | undefined = undefined;
    if (buildingPart.toLowerCase().startsWith('p')) {
      const num = parseInt(buildingPart.slice(1), 10);
      if (!isNaN(num)) buildingNum = num;
    } else {
      const num = parseInt(buildingPart, 10);
      if (!isNaN(num)) buildingNum = num;
    }
    return { building: buildingNum, region: regionPart };
  }

  private updateSelectedSection(): void {
    const { building, region } = this.parseBuildingId(this.buildingId || '');
    if (building == null) {
      this.selectedSection = undefined;
      return;
    }
    if (region) {
      this.selectedSection = this.sections.find(s => s.building === building && String(s.region) === String(region));
    } else {
      this.selectedSection = this.sections.find(s => s.building === building);
    }
      this.fetchDesksForSelectedSection();
      this.populateEditableFields();
  }

  private populateEditableFields(): void {
    if (!this.selectedSection) {
      this.departmentInput = '';
      this.exceptionsInput = '';
    
      this.selectedAreas = [];
      this.selectedExceptions = [];
    
      this.resetOriginalValues();
      return;
    }
    
    this.selectedAreas = this.convertToChips(this.selectedSection.department);
    this.selectedExceptions = this.convertToChips(this.selectedSection.exceptions);
    
    this.departmentInput = '';
    this.exceptionsInput = '';
    this.statement = this.selectedSection.statement || '';
    this.statementSetFromIso(this.selectedSection.statement_start, this.selectedSection.statement_end);
    this.resetOriginalValues();
  }

  private resetOriginalValues(): void {
    this.originalSelectedAreas = this.selectedAreas.map(function(area) { return area.value; });
    this.originalSelectedExceptions = this.selectedExceptions.map(function(exception) { return exception.value; });
    this.originalBlocked = !!this.selectedSection?.blocked;
    this.originalCheckin = !!this.selectedSection?.checkin;
    this.originalStatement = this.statement;
    this.originalStatementStart = this.statementStart;
    this.originalStatementEnd = this.statementEnd;
    this.checkForChanges();
    this.checkCommunicationChanges();
  }

  private addArea(): void {
    const value = this.departmentInput.trim();
    
    if (value === '') {
      return;
    }
    
    const alreadyExists = this.selectedAreas.some(function(area) {
      return area.value.toUpperCase() === value.toUpperCase();
    });
    
    if (alreadyExists) {
      this.departmentInput = '';
      return;
    }
    
    this.selectedAreas.push({
      value: value,
      label: value
    });
    
    this.departmentInput = '';
    
    this.checkForChanges();
  }
    
  private addException(): void {
    const value = this.exceptionsInput.trim();
    
    if (value === '') {
      return;
    }
    
    const alreadyExists = this.selectedExceptions.some(function(exception) {
      return exception.value.toUpperCase() === value.toUpperCase();
    });
    
    if (alreadyExists) {
      this.exceptionsInput = '';
      return;
    }
    
    this.selectedExceptions.push({
      value: value,
      label: value
    });
    
    this.exceptionsInput = '';
    
    this.checkForChanges();
  }

  private statementClearDates(): void {
    this.statementStart = '';
    this.statementEnd = '';
  }

  private statementSetFromIso(start?: string, end?: string): void {
    this.statementStart = start ? this.isoToDateInput(start) : '';
    this.statementEnd = end ? this.isoToDateInput(end) : '';
  }
    
  private isoToDateInput(iso: string): string {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return '';
    }
  }

  private dateInputToIso(dateInput: string): string | undefined {
    if (!dateInput) return undefined;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString();
  }
    
  private fetchDesksForSelectedSection(): void {
    if (!this.selectedSection || this.selectedSection.id == null) {
      this.apiDesks.set([]);
      return;
    }
    this.adminApi.getDesks(this.selectedSection.id).subscribe({
      next: (desks) => {
        this.apiDesks.set(desks);
        this.selectedDesks.clear();
      },
      error: () => {
        this.apiDesks.set([]);
        Swal.fire({ icon: 'error', title: 'Erro ao carregar mesas', text: 'Tente novamente.' });
      }
    });
  }

  private convertToChips(value: string[] | string | null | undefined): { value: string; label: string }[] {
    let values: string[] = [];
  
    if (Array.isArray(value)) {
      values = value;
    } else if (typeof value === 'string') {
      values = value.split(',');
    }
  
    return values
      .map(function(item) {
        return item.trim();
      })
      .filter(function(item) {
        return item !== '';
      })
      .map(function(item) {
        return {
          value: item,
          label: item
        };
      });
  }
     
}
